import {
    Flex,
    List,
    Button,
    useStyles,
    Caption,
    useTheme,
    pxToRem,
} from '@archipro-design/aria';
import { StarFill } from '@archipro-design/icons';
import { Link, useLoaderData } from '@remix-run/react';
import { localizeTime } from '@modules/root';
import * as S from './Stats.style';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import type { loader } from '~/routes/_app.product.$urlSegment';
import type { ProductProfessional } from '../../type/product-professional';
import { useSaveToDesignBoard } from '~/modules/design-board';
import { useClickToReveal } from '~/modules/root/hook/use-click-to-reveal';

import cx from 'classnames';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';

type CompanyInfoData = Pick<
    ProductProfessional,
    'URLSegment' | 'LibraryLocations' | 'ProfileLiveDate' | 'ID' | 'Website'
>;
interface CompanyInfoStatsProps {
    data: CompanyInfoData;
}

const resolveSuburbCity = (locations: CompanyInfoData['LibraryLocations']) => {
    if (locations && locations.length > 0) {
        const { Suburb, City, Region } = locations[0]!;

        return [Suburb, Region ?? City].filter(Boolean).join(', ');
    }
    return '';
};

const getMetricData = (data: CompanyInfoData) => {
    const profilePage = `/professional/${data.URLSegment}`;
    const webUrl = data.Website;
    const suburbCity = resolveSuburbCity(data.LibraryLocations);
    const locationsCount = data.LibraryLocations.length ?? 0;
    const memberSince = localizeTime(data.ProfileLiveDate).format('YYYY');

    return {
        suburbCity,
        locationsCount,
        webUrl,
        profilePage,
        memberSince,
    };
};

const CompanyInfoStats = ({ data }: CompanyInfoStatsProps) => {
    const { css } = useStyles();
    const { siteVariables } = useTheme();

    const REVIEWS_READY = false;

    const { suburbCity, locationsCount, webUrl, profilePage, memberSince } =
        getMetricData(data);

    const { favouriteItem } = useSaveToDesignBoard({
        itemType: 'ProfessionalPin',
        itemId: data.ID,
    });

    const { product } = useLoaderData<typeof loader>();

    const { onRevealClick } = useClickToReveal({
        revealType: 'Location',
        revealKey: `professional-${product.Professional.ID}`,
    });

    const {
        referralTrackingURL,
        referralTrackingClass,
        referralTrackingParams,
    } = useWebsiteReferralTracking(webUrl, {
        libraryItemID: product.ID,
        professionalTitle: product.Professional.Title,
    });

    const companyInfoStats = [
        {
            label: 'ArchiPro Member since',
            customChildComponent: (
                <Flex data-testid="member-since">{memberSince}</Flex>
            ),
        },
        // ECOM-1087/1088: OUT OF SCOPE.
        ...(REVIEWS_READY
            ? [
                  {
                      label: 'Reviews',
                      customChildComponent: (
                          <Flex>
                              <Flex className={css(S.ReviewsStarContainer)}>
                                  {[...Array(5)].map((_, i) => (
                                      <StarFill
                                          key={`review-star-${i}`}
                                          className={css(S.StarIcon)}
                                      />
                                  ))}
                              </Flex>

                              <Caption className={css(S.ReviewsCount)}>
                                  (6 reviews)
                              </Caption>
                          </Flex>
                      ),
                  },
              ]
            : []),

        ...(suburbCity
            ? [
                  {
                      label: 'Location',
                      customChildComponent: (
                          <Link
                              data-testid="location"
                              style={{
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  color: 'inherit',
                              }}
                              to={`${product.Professional.Link}/contact#${PROFESSIONAL_SECTIONS_ANCHOR}`}
                              onClick={() => onRevealClick()}
                          >
                              {locationsCount > 1
                                  ? 'View all locations'
                                  : 'View address'}{' '}
                              {locationsCount > 1 &&
                                  `(+${String(locationsCount - 1)})`}
                          </Link>
                      ),
                  },
              ]
            : []),
        {
            label: 'More information',
            customChildComponent: (
                <Flex space="between" className={css(S.WebsiteStat)}>
                    {webUrl ? (
                        <Button
                            as="a"
                            href={referralTrackingURL}
                            target="_blank"
                            size={16}
                            text
                            color="dark"
                            variables={{
                                darkButtonTextColorHover:
                                    siteVariables.colors.grey['200'],
                                buttonPadding: 0,
                            }}
                            className={cx(
                                css({
                                    textDecoration: 'underline!important',
                                }),
                                referralTrackingClass
                            )}
                            data-testid="website-link"
                            {...referralTrackingParams}
                        >
                            Visit Product website
                        </Button>
                    ) : (
                        <div />
                    )}
                    <Flex
                        vAlign="center"
                        gap="space-24"
                        className={css(S.FavouriteGroup)}
                    >
                        <Button
                            color="dark"
                            className={css(S.ProfileButton)}
                            as={Link}
                            to={profilePage}
                            size={16}
                            variables={{
                                buttonSize24LetterSpacing:
                                    siteVariables.letterSpacingMedium,
                            }}
                        >
                            View Profile
                        </Button>
                        <SaveToDesignBoard
                            {...favouriteItem}
                            heartButton={{
                                className: css(S.HeartButton),
                                variables: {
                                    buttonSize36IconXMargin: '0',
                                    buttonPadding: pxToRem(8),
                                },
                            }}
                        />
                    </Flex>
                </Flex>
            ),
        },
    ];

    return (
        <div className={css(S.CompanyInfoContainer)}>
            <List
                items={companyInfoStats}
                className={css(S.CompanyInfoList)}
                variables={{
                    listBorderColor: siteVariables.colors.primitive.black,
                }}
            />
        </div>
    );
};

export default CompanyInfoStats;
